/**
 * Spending Status System
 * Reusable across dashboard, forecast, notifications
 */

// Status thresholds (in currency)
const THRESHOLDS = {
  ON_TRACK: 20,    // >= £20 safe to spend
  TIGHT: 5,        // >= £5 safe to spend
  // Below £5 = Review needed
};

// Status definitions
export const SPENDING_STATUS = {
  ON_TRACK: {
    key: 'on_track',
    emoji: '🟢',
    label: 'On Track',
    message: "You're on track until payday.",
    color: '#5EEAD4',
    bgColor: 'bg-[#5EEAD4]/10',
    textColor: 'text-[#5EEAD4]',
  },
  TIGHT: {
    key: 'tight',
    emoji: '🟠',
    label: 'Tight - be careful',
    message: 'Your daily safe spend is getting tight.',
    color: '#F59E0B',
    bgColor: 'bg-[#F59E0B]/10',
    textColor: 'text-[#F59E0B]',
  },
  REVIEW_NEEDED: {
    key: 'review_needed',
    emoji: '🔴',
    label: 'Review needed',
    message: 'Your budget needs attention.',
    color: '#EF4444',
    bgColor: 'bg-[#EF4444]/10',
    textColor: 'text-[#EF4444]',
  },
  LOADING: {
    key: 'loading',
    emoji: '⏳',
    label: 'Loading',
    message: 'Calculating your budget...',
    color: '#9CA3AF',
    bgColor: 'bg-[#9CA3AF]/10',
    textColor: 'text-[#9CA3AF]',
  },
};

/**
 * Get spending status based on safe-to-spend amount
 * @param {number|null} safeToSpend - The safe to spend amount
 * @returns {object} Status object with emoji, label, message, colors
 */
export const getSpendingStatus = (safeToSpend) => {
  if (safeToSpend === null || safeToSpend === undefined) {
    return SPENDING_STATUS.LOADING;
  }

  if (safeToSpend >= THRESHOLDS.ON_TRACK) {
    return SPENDING_STATUS.ON_TRACK;
  }

  if (safeToSpend >= THRESHOLDS.TIGHT) {
    return SPENDING_STATUS.TIGHT;
  }

  return SPENDING_STATUS.REVIEW_NEEDED;
};

/**
 * Format notification message with status
 * @param {number} safeToSpend - The safe to spend amount
 * @param {string} currency - Currency symbol (default: £)
 * @returns {object} Notification content { title, body, emoji, status }
 */
export const formatNotification = (safeToSpend, currency = '£') => {
  const status = getSpendingStatus(safeToSpend);
  const formattedAmount = `${currency}${safeToSpend.toFixed(2)}`;

  return {
    emoji: status.emoji,
    title: `${status.emoji} ${status.label}`,
    body: `${formattedAmount} safe to spend today.`,
    status: status.key,
    amount: formattedAmount,
    fullMessage: `${status.emoji} ${status.label}\n${formattedAmount} safe to spend today.`,
  };
};

/**
 * Format dashboard status display
 * @param {number} safeToSpend - The safe to spend amount
 * @returns {object} Dashboard display content
 */
export const formatDashboardStatus = (safeToSpend) => {
  const status = getSpendingStatus(safeToSpend);

  return {
    emoji: status.emoji,
    label: status.label,
    message: status.message,
    color: status.color,
    bgColor: status.bgColor,
    textColor: status.textColor,
    key: status.key,
  };
};

/**
 * Get status for forecast/cards display
 * @param {number} safeToSpend - The safe to spend amount
 * @param {number} daysUntilPayday - Days remaining until payday
 * @returns {object} Forecast status with additional context
 */
export const getForecastStatus = (safeToSpend, daysUntilPayday) => {
  const status = getSpendingStatus(safeToSpend);
  
  let contextMessage = status.message;
  
  if (daysUntilPayday <= 2) {
    contextMessage = status.key === 'on_track' 
      ? 'Almost at payday - looking good!' 
      : `${daysUntilPayday} day${daysUntilPayday === 1 ? '' : 's'} until payday.`;
  }

  return {
    ...status,
    contextMessage,
    daysUntilPayday,
  };
};

export default {
  SPENDING_STATUS,
  THRESHOLDS,
  getSpendingStatus,
  formatNotification,
  formatDashboardStatus,
  getForecastStatus,
};
